import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { Upload, Trash2, Loader2, Truck, ImageOff } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const MAX_PHOTOS = 8;

export const fileUrl = (path) => `${API}/files/${path}`;

/** Transporter fleet (van/truck) photo manager — upload, preview, delete. */
const FleetPhotosManager = ({ userId }) => {
  const { t } = useTranslation();
  const [photos, setPhotos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const inputRef = useRef(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/transporters/${userId}/vehicle-photos`);
      setPhotos(data.vehicle_photos || []);
    } catch {
      setPhotos([]);
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const onPick = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    setError('');
    if (!['image/jpeg', 'image/png', 'image/webp'].includes(file.type)) {
      setError(t('transporter.photoType', 'Only JPEG, PNG or WEBP images are allowed.'));
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setError(t('transporter.photoSize', 'Image must be 5MB or smaller.'));
      return;
    }
    setUploading(true);
    try {
      const form = new FormData();
      form.append('file', file);
      await axios.post(`${API}/transporters/${userId}/vehicle-photos`, form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      await load();
    } catch (err) {
      setError(err.response?.data?.detail || t('transporter.photoUploadFailed', 'Upload failed. Please try again.'));
    } finally {
      setUploading(false);
    }
  };

  const removePhoto = async (path) => {
    setError('');
    try {
      await axios.delete(`${API}/transporters/${userId}/vehicle-photos`, { data: { path } });
      setPhotos((p) => p.filter((x) => x !== path));
    } catch {
      setError(t('transporter.photoDeleteFailed', 'Could not remove the photo.'));
    }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-2xl p-6" data-testid="fleet-photos-panel">
      <div className="flex items-start justify-between gap-4 mb-2 flex-wrap">
        <div>
          <h2 className="text-xl font-bold text-white flex items-center gap-2">
            <Truck className="w-5 h-5 text-amber-500" />
            {t('transporter.fleetPhotos', 'Fleet Photos')}
          </h2>
          <p className="text-gray-400 text-sm mt-1">
            {t('transporter.fleetPhotosHint', 'Add photos of your vans and trucks. Customers see these on your quotes.')}
          </p>
        </div>
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={uploading || photos.length >= MAX_PHOTOS}
          data-testid="fleet-upload-btn"
          className="bg-amber-500 hover:bg-amber-600 disabled:opacity-50 text-black font-bold rounded-full px-5 py-2.5 text-sm flex items-center gap-2"
        >
          {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
          {t('transporter.addPhoto', 'Add photo')}
        </button>
        <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={onPick} data-testid="fleet-file-input" />
      </div>

      <p className="text-gray-500 text-xs mb-4">{photos.length}/{MAX_PHOTOS} {t('transporter.photos', 'photos')}</p>

      {error && (
        <div className="mb-4 text-sm text-red-400 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="fleet-error">{error}</div>
      )}

      {loading ? (
        <div className="flex items-center justify-center py-12 text-gray-400 gap-2">
          <Loader2 className="w-5 h-5 animate-spin" /> {t('common.loading', 'Loading…')}
        </div>
      ) : photos.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-12 text-gray-500" data-testid="fleet-empty">
          <ImageOff className="w-10 h-10 mb-2" />
          <p className="text-sm">{t('transporter.noPhotos', 'No vehicle photos yet. Add your first one above.')}</p>
        </div>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 gap-3" data-testid="fleet-grid">
          {photos.map((path) => (
            <div key={path} className="relative group rounded-xl overflow-hidden border border-white/10 bg-black/30 aspect-[4/3]" data-testid={`fleet-photo-${path.split('/').pop()}`}>
              <img src={fileUrl(path)} alt="Vehicle" className="w-full h-full object-cover" loading="lazy" />
              <button
                type="button"
                onClick={() => removePhoto(path)}
                data-testid="fleet-delete-btn"
                className="absolute top-1.5 right-1.5 bg-black/70 hover:bg-red-600 text-white rounded-full p-1.5 opacity-0 group-hover:opacity-100 transition-opacity"
                title={t('common.delete', 'Delete')}
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default FleetPhotosManager;
